import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut, Settings, ChevronDown, History, CreditCard, MessageSquare, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const UserMenu = ({ onOpenSettings, onOpenChatHistory, onOpenUpgrade, onOpenFeedback, onOpenChangelog, collapsed = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const handleMenuClick = (action) => {
    setIsOpen(false);
    if (action === 'settings') {
      onOpenSettings();
    } else if (action === 'history') {
      onOpenChatHistory();
    } else if (action === 'upgrade') {
      onOpenUpgrade?.();
    } else if (action === 'feedback') {
      onOpenFeedback?.();
    } else if (action === 'changelog') {
      onOpenChangelog?.();
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-200 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${isOpen ? 'bg-gray-100 dark:bg-gray-700' : ''} ${collapsed ? 'justify-center' : ''}`}
        title={collapsed ? user.email : ''}
      >
        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
          {user.email.charAt(0).toUpperCase()}
        </div>
        {!collapsed && (
          <>
            <span className="text-sm font-medium truncate max-w-32">
              {user.email}
            </span>
            <ChevronDown 
              size={16} 
              className={`transform transition-transform duration-200 ${isOpen ? '' : 'rotate-180'}`} 
            />
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 bottom-full mb-2 w-60 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50 animate-in slide-in-from-bottom-2 duration-200">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Signed in as
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {user.email}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <button
              onClick={() => handleMenuClick('changelog')}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Bell size={16} className="mr-3" />
              What's New
            </button>

            <button
              onClick={() => handleMenuClick('history')}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <History size={16} className="mr-3" />
              Chat History
            </button>

            <button
              onClick={() => handleMenuClick('settings')}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Settings size={16} className="mr-3" />
              Settings
            </button>

            <button
              onClick={() => handleMenuClick('upgrade')}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <CreditCard size={16} className="mr-3" />
              Account & Billing
            </button>

            <button
              onClick={() => handleMenuClick('feedback')}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <MessageSquare size={16} className="mr-3" />
              Feedback
            </button>

            <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <LogOut size={16} className="mr-3" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;